/**
 * Rental-agreement (חוזה שכירות) arithmetic for a RENTED listing — pure, so the form, the
 * chip, the tab and the banner all read one clock.
 *
 * ── Compared on the ISRAEL CALENDAR DAY, never on raw instants ──────────────────────────
 *
 * `leaseStartDate` / `leaseEndDate` are date-only and stored as SOME midnight — UTC
 * midnight from this form and from the AI's `update_listing`, Israel-local midnight from
 * older clients — exactly like `exclusivity` (see `crmExclusivity.ts`, and
 * `listing-lease-reminders.md` in `webot-server`). The server's reminder job resolves both
 * sides to their Asia/Jerusalem day; anything here that counted differently would show
 * "ends in 60 days" on a morning the WhatsApp said 59.
 *
 * Days travel as `YYYY-MM-DD` strings, which is also what the form's date inputs hold.
 */

import {
  LEASE_REMINDER_OFFSETS_IN_DAYS,
  LEASE_REMINDER_WINDOW_IN_DAYS
} from "../../@types/sharedSchema";

const MS_PER_DAY = 86_400_000;

const israelDay = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jerusalem",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});

/** The instant's Israel calendar day, as `YYYY-MM-DD`. */
export function israelDayOf(date: Date): string {
  return israelDay.format(date);
}

function dayToUtcMs(day: string): number {
  const [year, month, date] = day.split("-").map(Number);
  return Date.UTC(year, month - 1, date);
}

function utcMsToDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Whole days from `fromDay` to `toDay`. Negative when `toDay` is the earlier one. */
export function daysBetween(fromDay: string, toDay: string): number {
  return Math.round((dayToUtcMs(toDay) - dayToUtcMs(fromDay)) / MS_PER_DAY);
}

export function addDays(day: string, days: number): string {
  return utcMsToDay(dayToUtcMs(day) + days * MS_PER_DAY);
}

/** `30.11.2027` — all-numeric, so no Hebrew month name can reorder under RTL. */
export function formatLeaseDay(day: string, shortYear = false): string {
  const [year, month, date] = day.split("-").map(Number);
  return `${date}.${month}.${shortYear ? String(year).slice(-2) : year}`;
}

/**
 * The last day of a lease that starts on `startDay` and runs `months` months: a year from
 * 1.12.2026 ends on 30.11.2027, the day before the anniversary. A start day the target
 * month does not have (the 31st, 29.2) uses that month's last day as the anniversary.
 */
export function leaseEndDayForPeriod(startDay: string, months: number): string {
  const [year, month, date] = startDay.split("-").map(Number);
  const targetMonthStart = new Date(Date.UTC(year, month - 1 + months, 1));
  const targetYear = targetMonthStart.getUTCFullYear();
  const targetMonth = targetMonthStart.getUTCMonth();
  const lastDateOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const anniversaryMs = Date.UTC(targetYear, targetMonth, Math.min(date, lastDateOfTargetMonth));

  return utcMsToDay(anniversaryMs - MS_PER_DAY);
}

/** Which of `periods` (in months) a start/end pair is exactly, so its button reads as chosen. */
export function matchLeasePeriod(
  startDay: string | null,
  endDay: string | null,
  periods: readonly number[]
): number | null {
  if (!startDay || !endDay) return null;

  return periods.find((months) => leaseEndDayForPeriod(startDay, months) === endDay) ?? null;
}

/**
 * How far ahead a lease counts as "ending soon" on every surface: from the day the first
 * WhatsApp reminder goes out, so the chip and the banner are already up when it lands.
 */
export const LEASE_ENDING_SOON_WINDOW_IN_DAYS = Math.max(...LEASE_REMINDER_OFFSETS_IN_DAYS);

/** Whole days from today's Israel day to the lease's last day. 0 = ends today, negative = ended. */
export function leaseDaysLeft(leaseEnd: Date, now: Date = new Date()): number {
  return daysBetween(israelDayOf(now), israelDayOf(leaseEnd));
}

export type LeaseState = "missing" | "active" | "endingSoon" | "ended";

/** Where a RENTED listing's agreement stands. The caller has already checked it is rented. */
export function getLeaseState(leaseEnd: Date | null | undefined, now: Date = new Date()): LeaseState {
  if (!leaseEnd) return "missing";

  const daysLeft = leaseDaysLeft(leaseEnd, now);

  if (daysLeft < 0) return "ended";

  return daysLeft <= LEASE_ENDING_SOON_WINDOW_IN_DAYS ? "endingSoon" : "active";
}

export interface LeaseReminderPlan {
  offsetDays: number;
  /** The reminder's own day: `offsetDays` before the lease ends. */
  day: string;
  /**
   * - `onDay`: still ahead — it goes out that morning.
   * - `soon`: its day is today or just behind us, and the job still sends it tomorrow.
   * - `passed`: too late for this one; nothing will be sent for it.
   */
  when: "onDay" | "soon" | "passed";
}

/**
 * When each WhatsApp reminder of a lease ending on `endDay` goes out, by the same rule the
 * server's job applies: due on its day and for `LEASE_REMINDER_WINDOW_IN_DAYS` days in all.
 *
 * Today's run may already be behind us when the realtor saves, so a reminder whose day is
 * today or earlier is promised only when TOMORROW's run would still send it. This is the
 * line the form shows under the dates — it must never promise a message that will not come.
 */
export function planLeaseReminders(endDay: string, todayDay: string): LeaseReminderPlan[] {
  return LEASE_REMINDER_OFFSETS_IN_DAYS.map((offsetDays): LeaseReminderPlan => {
    const day = addDays(endDay, -offsetDays);
    const daysLate = daysBetween(day, todayDay);

    if (daysLate < 0) return { offsetDays, day, when: "onDay" };

    return { offsetDays, day, when: daysLate + 1 < LEASE_REMINDER_WINDOW_IN_DAYS ? "soon" : "passed" };
  });
}
